/**
 * Lightweight pub/sub so product screens can ask the primary nudge to re-fetch
 * after a meaningful success (guided lane).
 *
 * Prefer success emits (`supplier_added` / Phase 2 first-success saves).
 * Route-leave is a safety net when the user navigates away without an emit.
 * See express_app/services/growthEngine/docs/guidedLane.md.
 */

/** Delay before first guided re-fetch (lets writes settle; softer UX). */
export const GROWTH_ENGINE_NUDGE_REFRESH_DELAY_MS = 1000

/** One deferred retry when the ladder still returns the same clicked intent. */
export const GROWTH_ENGINE_NUDGE_REFRESH_RETRY_MS = 4000

/** Canonical `reason` values for guided-lane refresh events. */
export const GROWTH_ENGINE_GUIDED_LANE_REASONS = Object.freeze({
  SUPPLIER_ADDED: 'supplier_added',
  PAR_SAVED: 'par_saved',
  RECIPE_SAVED: 'recipe_saved',
  STOCK_TAKE_CREATED: 'stock_take_created',
  INVOICE_UPLOADED: 'invoice_uploaded',
  PO_RECEIPTED: 'po_receipted',
  SUPPLIER_RESTRICTED: 'supplier_restricted',
  ROUTE_LEAVE: 'route_leave',
  MANUAL: 'manual',
})

/**
 * Phase 2 first-success saves refresh Home even without a prior Home CTA click.
 * Standing orders are app-only — dashboard has no save path.
 */
export const GROWTH_ENGINE_FIRST_SUCCESS_REFRESH_REASONS = Object.freeze([
  GROWTH_ENGINE_GUIDED_LANE_REASONS.RECIPE_SAVED,
  GROWTH_ENGINE_GUIDED_LANE_REASONS.PAR_SAVED,
  GROWTH_ENGINE_GUIDED_LANE_REASONS.STOCK_TAKE_CREATED,
  GROWTH_ENGINE_GUIDED_LANE_REASONS.INVOICE_UPLOADED,
  GROWTH_ENGINE_GUIDED_LANE_REASONS.PO_RECEIPTED,
  GROWTH_ENGINE_GUIDED_LANE_REASONS.SUPPLIER_RESTRICTED,
])

export function isGrowthEngineFirstSuccessRefreshReason(reason) {
  return GROWTH_ENGINE_FIRST_SUCCESS_REFRESH_REASONS.includes(reason)
}

const listeners = new Set()

/**
 * Strip query/hash and trailing slash for route compares.
 * @param {string|null|undefined} path
 */
export function normalizeGrowthEnginePath(path) {
  if (path == null) return ''
  const bare = String(path).split(/[?#]/)[0].trim()
  if (!bare || bare === '/') return bare || '/'
  return bare.replace(/\/+$/, '') || '/'
}

/**
 * True while the user is still on the CTA landing screen (or a nested path under it).
 * @param {string|null|undefined} pathname — current location.pathname
 * @param {string|null|undefined} landingPath — path from resolveGrowthEngineCtaPath
 */
export function isOnGrowthEngineCtaLanding(pathname, landingPath) {
  const current = normalizeGrowthEnginePath(pathname)
  const landing = normalizeGrowthEnginePath(landingPath)
  if (!current || !landing) return false
  return current === landing || current.startsWith(`${landing}/`)
}

/**
 * Ask the primary nudge (if awaiting after CTA) to re-fetch recommendations.
 * @param {{ venueId: string, reason?: string }} payload
 */
export function requestGrowthEngineNudgeRefresh(payload = {}) {
  const venueId = payload.venueId != null ? String(payload.venueId).trim() : ''
  if (!venueId) return

  const event = {
    venueId,
    reason: payload.reason ? String(payload.reason) : 'manual',
  }

  listeners.forEach(listener => {
    try {
      listener(event)
    } catch (err) {
      console.warn('Growth Engine nudge refresh listener failed', err)
    }
  })
}

/**
 * Convenience for product success paths (supplier connect, first-success saves).
 * No-op when venueId is missing. Phase 2 first-success reasons refresh Home
 * even without a prior CTA; other reasons only act while awaiting after CTA.
 * @param {string} venueId
 * @param {string} reason
 */
export function emitGrowthEngineGuidedLaneProgress(venueId, reason) {
  requestGrowthEngineNudgeRefresh({ venueId, reason })
}

/**
 * @param {(event: { venueId: string, reason: string }) => void} listener
 * @returns {() => void} unsubscribe
 */
export function subscribeGrowthEngineNudgeRefresh(listener) {
  if (typeof listener !== 'function') {
    return () => {}
  }
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Test helper — drop listeners between vitest cases. */
export function __resetGrowthEngineNudgeRefreshForTests() {
  listeners.clear()
}

// Console / QA ship check (non-production): after CTA, call
// window.requestGrowthEngineNudgeRefresh({ venueId, reason: 'manual' })
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  window.requestGrowthEngineNudgeRefresh = requestGrowthEngineNudgeRefresh
}
